import { Component, EventEmitter, inject, input, InputSignal, OnDestroy, OnInit, Output } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { DatePickerModule } from "primeng/datepicker";
import { SharedModule } from "src/app/shared/shared.module";
import { TalentPoolService } from "src/app/services/talent-pool.service";
import { GlobalServicesService } from "src/app/services/global-services.service";
import { MessageService } from "primeng/api";
import { Ripple } from "primeng/ripple";
import saveAs from "file-saver";
import { FileUploadService } from "src/app/services/file-upload.service";
import { SharedServicesService } from "src/app/services/shared-services.service";

type Type = "single" | "bulk-invite" | "bulk-registration";
const ageValidator: ValidatorFn = (control: AbstractControl): { [key: string]: any } | null => {
    if (!control.value) return null;
    let dateOfBirth: Date;
    if (typeof control.value === 'string') {
        dateOfBirth = new Date(control.value);
    } else {
        dateOfBirth = control.value;
    }
    if (isNaN(dateOfBirth.getTime())) return { 'ageInvalid': true };
    const today = new Date();
    const birthYear = dateOfBirth.getFullYear();
    const birthMonth = dateOfBirth.getMonth();
    const birthDay = dateOfBirth.getDate();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();
    let age = currentYear - birthYear;
    if (currentMonth < birthMonth || (currentMonth === birthMonth && currentDay < birthDay)) {
        age--;
    }

    return age < 14 ? { 'ageInvalid': true } : null; // Age should be at least 14
};

@Component({
    selector: 'app-invite-candidate-v2',
    templateUrl: './invite-candidate-v2.html',
    styleUrls: ['./invite-candidate-v2.css'],
    providers: [],
    standalone: true,
    imports: [DialogModule, ButtonModule, CardModule, ReactiveFormsModule, InputTextModule, SelectModule, DatePickerModule, SharedModule, Ripple]
})
export class InviteCandidateV2Component implements OnInit, OnDestroy {
    unsub$: Subject<void> = new Subject<void>();
    emailPattern: any = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/;
    genderList = [
        { label: 'Male', value: 'male' },
        { label: 'Female', value: 'female' },
        { label: 'Other', value: 'other' },
    ]
    currentDate = new Date();
    recruiterId: number | null = null;
    companyId: string = '';
    sendingInvite: boolean = false;

    private route = inject(ActivatedRoute);
    private fb = inject(FormBuilder);
    private talentPoolService = inject(TalentPoolService);
    private globalService = inject(GlobalServicesService);
    private messageService = inject(MessageService);
    private fileUploadService = inject(FileUploadService);
    private sharedServicesService = inject(SharedServicesService);

    from: InputSignal<"header" | "events"> = input.required();
    @Output() close$ = new EventEmitter<any>();

    showTypeSelection: boolean = true;
    showForm: boolean = true;
    type: Type = "single";
    eventId: string | null = null;

    inviteForm!: FormGroup;
    loadEventList: boolean = false;
    isLoadingMoreEvents: boolean = false;
    eventsPage: number = 1;
    totalEventPages: number = 1;
    hasMoreEvents: boolean = true;
    currentSearchTerm: string = "";
    eventSearchSubject = new Subject<string>();
    eventList: any = [];
    eventCategories: any = [];
    sourceNameList: any = [];

    // Bulk Upload
    cohortUploaded: boolean = false;
    countDiv: boolean = false;
    bulkEnable: boolean = false;
    cohortFile: Array<any> = [];
    cohortFileCopy: Array<any> = [];
    uploadingFile: boolean = false;
    fileUploadPayload: any;
    cohortFileSize: any;

    disableInvite: boolean = false;
    bulkInviteLimit: number = 5000;

    constructor() { }

    ngOnInit(): void {
        this.companyId = localStorage.getItem('companyUUID') ?? '';
        this.type = this.from() === "events" ? "bulk-invite" : "single"

        this.route.params.pipe(takeUntil(this.unsub$)).subscribe((params) => {
            const { eventId } = params;
            this.eventId = eventId;
        });

        this.globalService.userMeta$.pipe(takeUntil(this.unsub$)).subscribe(meta => {
            this.recruiterId = meta?.user_profile?.id || '';
        });

        this.sharedServicesService.metadata$.pipe(takeUntil(this.unsub$)).subscribe(metadata => {
            if (metadata?.bulk_candidate_invite_limit) {
                this.bulkInviteLimit = metadata.bulk_candidate_invite_limit;
            }
        });

        this.eventSearchSubject.pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.unsub$)).subscribe(searchTerm => {
            const trimmedValue = searchTerm.trim();
            if (trimmedValue.length === 0) {
                this.currentSearchTerm = '';
                this.searchEvents('');
            } else if (trimmedValue.length > 2) {
                this.currentSearchTerm = trimmedValue;
                this.searchEvents(trimmedValue);
            }
        });

        this.getInviteFormData();
    }

    ngOnDestroy(): void {
        this.unsub$.next();
        this.unsub$.complete();
    }

    changeActiveType(newType: Type) {
        this.type = newType;
    }

    toggleTypeSelection() {
        this.showTypeSelection = !this.showTypeSelection;
        this.onRemoveExcel();

        if (!this.showTypeSelection) {
            this.eventsPage = 1;
            if (this.type === "single") {
                this.inviteForm = this.fb.group({
                    firstName: new FormControl('', [Validators.minLength(3), Validators.maxLength(50)]),
                    lastName: new FormControl('', [Validators.minLength(1), Validators.maxLength(50)]),
                    email: new FormControl('', [Validators.required, Validators.pattern(this.emailPattern)]),
                    mobileNumber: new FormControl(null, [Validators.pattern("^[0-9]{10}$")]),
                    eventCategory: new FormControl(null, this.eventId ? null : Validators.required),
                    gender: new FormControl(null),
                    date_of_birth: new FormControl(null, [ageValidator]),
                    eventId: new FormControl(this.eventId || '', Validators.required),
                    sourceName: new FormControl(null, Validators.required),
                });
            } else {
                this.inviteForm = this.fb.group({
                    eventCategory: new FormControl(null, this.eventId ? null : Validators.required),
                    eventId: new FormControl(this.eventId || '', Validators.required),
                });
            }
        }

        if(this.showTypeSelection) {
            this.showForm = true;
        }
    }

    restrictNumber(event: any) {
        let e: any = event || window.event;
        let key = e.keyCode || e.which;

        if (key < 48 || key > 57 || event.target.value.length >= 10) {
            if (e.preventDefault) e.preventDefault();
            e.returnValue = false;
        }
    }

    eventChange(event: any) {
        this.inviteForm.controls['eventId'].setValue(event.value);
    }

    getInviteFormData = async () => {
        this.talentPoolService.getCandidateInviteFormDataFastApi().subscribe({
            next: (res: any) => {
                if (res.status === 200) {
                    const mapForms = (forms: any[]) => forms.map((element: any) => ({
                        label: element.name,
                        value: element.id,
                    }));
                    const eventCategories = res.body.event_categories || [];
                    this.eventCategories = mapForms(eventCategories);
                    const sourceNameList = res.body.candidate_sources || [];
                    this.sourceNameList = mapForms(sourceNameList);
                }
            },
            error: () => { }
        });
    }

    getAllEvents(event: any) {
        let categoryId = event?.value?.value || event?.value;
        if (!categoryId || (typeof categoryId !== 'string' && typeof categoryId !== 'number')) {
            return;
        }

        this.eventsPage = 1;
        this.loadEventsPage(categoryId, '');
        this.inviteForm.get("eventId")?.setValue("");
        this.inviteForm.get("eventId")?.updateValueAndValidity();
    }

    loadEventsPage(categoryId: string | number, searchTerm: string = '', appendToList: boolean = false) {
        const isLoadingMore = this.eventsPage > 1;
        if (isLoadingMore) {
            this.isLoadingMoreEvents = true;
        } else {
            this.loadEventList = true;
        }

        this.talentPoolService.getAllEventsByCategory(
            categoryId,
            this.eventsPage,
            10,
            'asc',
            'name',
            searchTerm,
            'all'
        ).subscribe({
            next: (res: any) => {
                if (res.status === 200 && res.body?.items) {
                    const events = res.body.items;
                    const mappedEvents = Array.isArray(events) && events.length > 0
                        ? events.map((event: any) => ({
                            label: event.name,
                            value: event.uuid,
                        }))
                        : [];

                    if (appendToList) {
                        this.eventList = this.eventList.filter((item: any) => item.value !== 'LOAD_MORE');
                        this.eventList = [...this.eventList, ...mappedEvents];
                    } else {
                        this.eventList = mappedEvents.length > 0
                            ? mappedEvents
                            : [{ label: "No events found", value: null }];
                    }

                    this.totalEventPages = res.body.pages || 1;
                    this.hasMoreEvents = this.eventsPage < this.totalEventPages;

                    if (this.hasMoreEvents && this.eventList.length > 0 && this.eventList[0].value !== null) {
                        this.eventList.push({
                            label: "Loading more events...",
                            value: 'LOAD_MORE',
                            disabled: true
                        });
                    }
                } else {
                    if (!appendToList) {
                        this.eventList = [{ label: "No events found", value: null }];
                    }
                    this.hasMoreEvents = false;
                }
                this.loadEventList = false;
                this.isLoadingMoreEvents = false;
            },
            error: (error) => {
                if (!appendToList) {
                    this.eventList = [{ label: "No events found", value: null }];
                }
                this.loadEventList = false;
                this.isLoadingMoreEvents = false;
                this.hasMoreEvents = false;
            }
        });
    }

    searchEvents(searchTerm: string) {
        this.eventsPage = 1;
        this.hasMoreEvents = true;
        this.currentSearchTerm = searchTerm;
        const currentCategoryId = this.inviteForm.get("eventCategory")?.value;

        this.loadEventsPage(currentCategoryId, searchTerm, false);
    }

    onEventDropdownFilter(event: any) {
        const searchTerm = event.filter || '';
        this.eventSearchSubject.next(searchTerm);
    }

    loadMoreEvents() {
        if (!this.hasMoreEvents || this.isLoadingMoreEvents) {
            return;
        }

        this.eventsPage = this.eventsPage + 1;
        const currentCategoryId = this.inviteForm.get("eventCategory")?.value;
        this.loadEventsPage(currentCategoryId, this.currentSearchTerm, true);
    }

    onEventDropdownShow(event: any) {
        setTimeout(() => {
            const dropdownPanels = document.querySelectorAll('.p-select-overlay .p-select-list-container');
            dropdownPanels.forEach(panel => {
                panel.addEventListener('scroll', this.handleDropdownScroll.bind(this));
            });
        }, 200);
    }

    onEventDropdownHide(event: any) {
        const dropdownPanels = document.querySelectorAll('.p-select-overlay .p-select-list-container');
        dropdownPanels.forEach(panel => {
            panel.removeEventListener('scroll', this.handleDropdownScroll.bind(this));
        });
    }

    handleDropdownScroll(event: any) {
        const element = event.target;
        const threshold = 100;

        if (element.scrollTop + element.clientHeight >= element.scrollHeight - threshold) {
            if (this.hasMoreEvents && !this.isLoadingMoreEvents) {
                setTimeout(() => {
                    if (this.hasMoreEvents && !this.isLoadingMoreEvents) {
                        this.loadMoreEvents();
                    }
                }, 300);
            }
        }
    }

    formatDate(dateString: string): string {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`; // Format: YYYY-MM-DD
    }

    generateFileName(file: any): string {
        let fileName = file.name.split(".");
        let fileExtension = fileName.pop();
        const timestamp = new Date().getTime();
        return `${fileName.join("").replace(/\s/g, "").replace(/[^\w\s]/gi, '')}_${timestamp}.${fileExtension}`;
    }

    // Bulk Registration
    sampleBulkRegistration() {
        const formValues = this.inviteForm.value;
        if (!formValues.eventId) {
            return;
        }

        this.talentPoolService.bulkRegistrationFileDownloadFastApi(formValues.eventId).subscribe({
            next: (res: any) => {
                if (res.status === 200) {
                    // Create a Blob from the response body
                    const blob = new Blob([res.body], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

                    // Extract the filename from Content-Disposition header
                    const contentDisposition = res.headers.get('content-disposition');
                    let filename = 'bulk_registration_data.xlsx';

                    if (contentDisposition) {
                        const matches = contentDisposition.match(/filename="(.+)"/);
                        if (matches?.[1]) {
                            filename = matches[1];
                        }
                    }

                    saveAs(blob, filename);
                }
            },
            error: (err) => {
                console.error('Error downloading the file:', err);
            }
        });
    }

    // Bulk Invite
    sampleBulkInvite() {
        this.talentPoolService.bulkInviteFileDownloadFastApi().subscribe({
            next: (res: any) => {
                if (res.status === 200) {
                    // Create a Blob from the response body
                    const blob = new Blob([res.body], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

                    // Extract the filename from Content-Disposition header
                    const contentDisposition = res.headers.get('content-disposition');
                    let filename = 'bulk_invite_data.xlsx';

                    if (contentDisposition) {
                        const matches = contentDisposition.match(/filename="(.+)"/);
                        if (matches?.[1]) {
                            filename = matches[1];
                        }
                    }

                    saveAs(blob, filename);
                }
            },
            error: (err) => {
                console.error('Error downloading the file:', err);
            }
        });
    }

    cohortUpload(event: any) {
        let file = event.addedFiles[0];
        if (!file) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No file selected' });
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'File size should be less than 2MB' });
            return;
        }

        const generatedFileName = this.generateFileName(file);
        const gcsFilePath = `temp/${this.companyId}/${generatedFileName}`;

        const payload = {
            file_path: gcsFilePath,
            bucket_type: "private",
            origin: "invite_candidate",
        };

        this.uploadingFile = true;

        this.fileUploadService.getSignedUrlGCS(payload).subscribe({
            next: (res: any) => {
                if (res?.body?.signed_url) {
                    this.uploadFileToGCS(res.body.signed_url, file, payload, generatedFileName);
                } else {
                    this.uploadingFile = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to get signed URL' });
                }
            },
            error: () => {
                this.uploadingFile = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error fetching signed URL' });
            }
        });
    }

    uploadFileToGCS(signedUrl: string, file: any, payload: any, updatedName: string) {
        const updatedPayload = {
            ...payload,
            file_name: updatedName,
            recruiter_id: this.recruiterId
        };

        this.fileUploadService.uploadUsingSignedUrlGCS(signedUrl, file).subscribe({
            next: (response: any) => {
                if (response.status === "success") {
                    this.fileUploadPayload = updatedPayload;
                    this.uploadingFile = false;
                    this.cohortUploaded = true;
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: "File uploaded." });
                }
            },
            error: (error) => {
                this.uploadingFile = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error uploading file.' });
            }
        });
    }

    onRemoveExcel() {
        this.cohortUploaded = false;
        this.countDiv = false;
        this.bulkEnable = false;
        this.cohortFile = [];
        this.cohortFileCopy = [];
    }

    // API Calls
    private showSuccess(detail: string): void {
        this.messageService.add({ severity: 'success', summary: 'Success', detail });
    }

    private showError(detail: string): void {
        this.messageService.add({ severity: 'error', summary: 'Error', detail });
    }

    private showInfo(detail: string): void {
        this.messageService.add({ severity: 'info', summary: 'Info', detail });
    }

    inviteCandidate(): void {
        this.inviteForm.markAllAsTouched();
        this.inviteForm.updateValueAndValidity();

        if (this.inviteForm.invalid) return;

        const { eventId } = this.inviteForm.value;
        if (!this.isValidEventId(eventId)) return;

        this.sendingInvite = true;

        switch (this.type) {
            case 'single':
                this.handleSingleInvite(eventId);
                break;

            case 'bulk-invite':
                this.handleBulkInvite(eventId, 'invite');
                break;

            default:
                this.handleBulkInvite(eventId, 'register');
                break;
        }
    }

    private isValidEventId(eventId: any): boolean {
        if (!eventId || typeof eventId !== 'string') {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Event ID is invalid'
            });
            this.sendingInvite = false;
            return false;
        }
        return true;
    }

    private handleSingleInvite(eventId: string): void {
        const payload = this.buildSingleInvitePayload();

        this.talentPoolService.candidateSingleInviteFastApi(eventId, payload)
            .subscribe({
                next: (res: any) => this.handleSingleInviteResponse(res),
                error: (err: any) => {
                    this.sendingInvite = false;
                }
            });
    }

    private buildSingleInvitePayload() {
        const v = this.inviteForm.value;
        return {
            first_name: v.firstName,
            last_name: v.lastName,
            email: v.email,
            gender: v.gender,
            date_of_birth: v.date_of_birth ? this.formatDate(v.date_of_birth) : null,
            mobile_number: v.mobileNumber,
            candidate_source_id: v.sourceName,
            recruiter_id: this.recruiterId
        };
    }

    private handleSingleInviteResponse(res: any): void {
        if (res.status === 201) {
            this.showSuccess(res.body.detail);
            this.close$.emit();
        } else if (res?.body?.data?.message === 'User exist in other events') {
            this.showInfo(res.body.data.message);
            this.disableInvite = true;
        } else {
            this.showError('Something went wrong');
            this.disableInvite = true;
        }
        this.sendingInvite = false;
    }

    private handleBulkInvite(eventId: string, mode: 'invite' | 'register'): void {
        if (!this.cohortUploaded) {
            this.showError('Please upload a file to proceed!');
            this.sendingInvite = false;
            return;
        }

        const payload = {
            file_name: this.fileUploadPayload.file_name,
            recruiter_id: this.recruiterId
        };

        const api$ = mode === 'invite'
            ? this.talentPoolService.bulkInviteFastApi(payload, eventId)
            : this.talentPoolService.bulkRegisterFastApi(payload, eventId);

        api$.subscribe({
            next: () => {
                this.showSuccess(
                    mode === 'invite'
                        ? 'Bulk Invite Sent Successfully!'
                        : 'Bulk Registration Successful!'
                );
                this.close$.emit();
                this.sendingInvite = false;
            },
            error: (error) => this.handleBulkError(error)
        });
    }

    private handleBulkError(error: any): void {
        this.sendingInvite = false;
        const err = error?.error;

        if (err?.detail === 'File not found') {
            this.showError('File not found. Please try uploading again.');
            return;
        }

        if (error.status === 500) {
            this.showError('Upload data in the provided sample format.');
            return;
        }

        this.showError(err?.detail || 'Something went wrong.');
    }
}